import { inject, Injectable, signal } from '@angular/core';
import { createClient, LiveClient, LiveTranscriptionEvents } from '@deepgram/sdk';
import { firstValueFrom, Subject } from 'rxjs';
import { TranscriptionRepository } from '@xmedical/core';
import { TRANSCRIBE_ERROR_HANDLER, TranscribeErrorHandler } from './error-handler.token';

/**
 * Deepgram streaming transcription service
 *
 * Handles real-time audio transcription using Deepgram's SDK
 * Connects directly to Deepgram using API key from backend
 */
@Injectable()
export class DeepgramStreamingService {
  private transcriptionRepo = inject(TranscriptionRepository);
  private errorHandler: TranscribeErrorHandler = inject(TRANSCRIBE_ERROR_HANDLER, { optional: true })
    ?? { error: (msg, err) => console.error(`[Transcribe] ${msg}`, err) };

  // Deepgram client and connection
  private deepgramClient?: ReturnType<typeof createClient>;
  private liveConnection?: LiveClient;
  private cachedApiKey?: string; // Cache API key in memory
  private disconnectPromise?: Promise<void>;

  // State signals
  readonly connected = signal<boolean>(false);
  readonly finalizing = signal<boolean>(false);
  readonly transcriptionText = signal<string>('');
  readonly apiKeyReady = signal<boolean>(false);
  readonly apiKeyLoading = signal<boolean>(false);

  // Observable streams
  private readonly transcriptionSubject = new Subject<{ text: string; isFinal: boolean; confidence: number }>();
  /** Emits transcription results as they arrive */
  readonly transcription$ = this.transcriptionSubject.asObservable();

  private readonly errorSubject = new Subject<string>();
  /** Emits transcription errors */
  readonly error$ = this.errorSubject.asObservable();

  /**
   * Pre-fetch API key from backend without establishing connection
   * Call this early (e.g., when permission is granted) to reduce latency when starting recording
   */
  async prefetchApiKey(): Promise<void> {
    // Already have key or currently loading
    if (this.cachedApiKey || this.apiKeyLoading()) {
      return;
    }

    this.apiKeyLoading.set(true);
    try {
      const creds = await firstValueFrom(this.transcriptionRepo.getTranscriptionCredentials());
      this.cachedApiKey = creds.apiKey;

      if (!this.cachedApiKey) {
        throw new Error('Failed to get Deepgram API key from backend');
      }

      this.apiKeyReady.set(true);
    } catch (error: unknown) {
      this.errorHandler.error('Failed to prefetch API key', error);
      throw error;
    } finally {
      this.apiKeyLoading.set(false);
    }
  }

  /**
   * Initialize connection to Deepgram
   */
  async connect(): Promise<void> {
    try {
      // Clear any accumulated text from a previous session
      this.reset();

      // Get API key from cache or fetch from backend
      if (!this.cachedApiKey) {
        await this.prefetchApiKey();
      }

      // Create Deepgram client
      this.deepgramClient = createClient(this.cachedApiKey);

      // Create live transcription connection
      // Don't specify encoding - let Deepgram auto-detect from WebM container
      const connection = this.deepgramClient.listen.live({
        model: 'nova-3-medical',
        language: 'en',
        // smart_format includes punctuation, numerals, and paragraphs
        smart_format: true,
        dictation: true,
        channels: 1,
        interim_results: true,
        utterance_end_ms: 1000,
        vad_events: true,
      });
      this.liveConnection = connection;

      // Set up event handlers
      this.setupEventHandlers(connection);

      // Wait for connection to open
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Connection timeout'));
        }, 10000);

        connection.on(LiveTranscriptionEvents.Open, () => {
          // Ignore stale connection events after disconnect/reconnect.
          if (this.liveConnection !== connection) {
            return;
          }
          clearTimeout(timeout);
          this.connected.set(true);
          resolve();
        });

        connection.on(LiveTranscriptionEvents.Error, (error: unknown) => {
          if (this.liveConnection !== connection) {
            return;
          }
          clearTimeout(timeout);
          reject(error);
        });
      });

    } catch (error: unknown) {
      this.errorHandler.error('Failed to connect to Deepgram', error);
      throw error;
    }
  }

  /**
   * Send audio chunk to Deepgram
   */
  sendAudio(audioBlob: Blob): void {
    if (!this.liveConnection || !this.connected()) {
      return;
    }
    // Convert Blob to ArrayBuffer and send
    audioBlob.arrayBuffer().then(buffer => {
      this.liveConnection!.send(buffer);
    }).catch(error => {
      this.errorHandler.error('Failed to send audio to Deepgram', error);
    });
  }

  /**
   * Close connection to Deepgram
   * Flushes the server's transcription buffer before closing to ensure
   * all formatted results (punctuation, smart_format) are received.
   */
  async disconnect(): Promise<void> {
    if (this.disconnectPromise) {
      return this.disconnectPromise;
    }

    this.disconnectPromise = this.performDisconnect();
    try {
      await this.disconnectPromise;
    } finally {
      this.disconnectPromise = undefined;
    }
  }

  private async performDisconnect(): Promise<void> {
    const connection = this.liveConnection;
    const client = this.deepgramClient;

    if (connection) {
      try {
        this.finalizing.set(true);

        // Arm the listener before flushing so a synchronous from_finalize event isn't missed
        const finalizedPromise = this.waitForFinalizedResult(connection);

        // Flush any transcription sitting in the server's buffer
        connection.finalize();

        // Wait for the finalized result (from_finalize: true) or timeout after 5s
        await finalizedPromise;

        // Request the server close the connection
        connection.requestClose();

        // Do not flip state for a newer session that replaced this connection.
        if (this.liveConnection === connection) {
          this.connected.set(false);
        }
      } catch (error: unknown) {
        this.errorHandler.error('Error closing Deepgram connection', error);
      } finally {
        this.finalizing.set(false);
      }
    }

    // Only clear shared references if this disconnect still owns them.
    if (this.liveConnection === connection) {
      this.liveConnection = undefined;
    }
    if (this.deepgramClient === client) {
      this.deepgramClient = undefined;
    }
  }

  /**
   * Wait for Deepgram to respond with the finalized transcript.
   * Resolves when a transcript with from_finalize: true arrives, or after 5s timeout.
   */
  private waitForFinalizedResult(connection: LiveClient): Promise<void> {
    return new Promise<void>(resolve => {
      let settled = false;

      const settle = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        connection.removeListener(LiveTranscriptionEvents.Transcript, handler);
        resolve();
      };

      const timeout = setTimeout(settle, 5000);

      const handler = (data: { from_finalize?: boolean }) => {
        if (data.from_finalize) {
          settle();
        }
      };

      connection.on(LiveTranscriptionEvents.Transcript, handler);
    });
  }

  /**
   * Reset service state
   */
  reset(): void {
    this.transcriptionText.set('');
  }

  /**
   * Set up Deepgram event handlers
   */
  private setupEventHandlers(connection: LiveClient): void {
    // Transcript event - receives transcription results
    connection.on(LiveTranscriptionEvents.Transcript, (data: {
      channel?: { alternatives?: Array<{ transcript?: string; confidence?: number }> };
      is_final?: boolean;
    }) => {
      if (this.liveConnection !== connection) {
        return;
      }
      const transcript = data.channel?.alternatives?.[0]?.transcript;
      const isFinal = data.is_final ?? false;
      const confidence = data.channel?.alternatives?.[0]?.confidence ?? 0;

      if (transcript && transcript.trim().length > 0) {
        // Emit the transcription result
        this.transcriptionSubject.next({
          text: transcript,
          isFinal,
          confidence
        });

        // Update accumulated text if final
        if (isFinal) {
          const currentText = this.transcriptionText();
          const newText = currentText ? `${currentText} ${transcript}` : transcript;
          this.transcriptionText.set(newText);
        }
      }
    });

    // Metadata event
    connection.on(LiveTranscriptionEvents.Metadata, () => {
      // Metadata received - no action needed
    });

    // Error event
    connection.on(LiveTranscriptionEvents.Error, (error: { message?: string }) => {
      if (this.liveConnection !== connection) {
        return;
      }
      this.errorSubject.next(error.message || 'Transcription error');
      this.errorHandler.error('Deepgram error', error);
    });

    // Close event
    connection.on(LiveTranscriptionEvents.Close, () => {
      if (this.liveConnection !== connection) {
        return;
      }
      this.connected.set(false);
    });

    // UtteranceEnd event - triggers when user stops speaking
    connection.on(LiveTranscriptionEvents.UtteranceEnd, () => {
      // Utterance ended - no action needed
    });
  }
}
